/**
 * /equipment/rental-links — Cross-equipment rental log.
 *
 * Independent of the per-item Rentals tab. Powers a controller's
 * question: "show me every rental we have on the Centennial project
 * this quarter."
 *
 * §7 / §9.1 of CONSTRUCTION-EQUIPMENT-COSTING-001 (rental subset of
 * Phase 2).
 */
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import {
  DataGridPro,
  type GridColDef,
  type GridRenderCellParams,
} from "@mui/x-data-grid-pro";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";
import {
  equipmentItemsQueryOptions,
  equipmentRentalLinkKeys,
  equipmentRentalLinksQueryOptions,
  partiesListQueryOptions,
} from "@/lib/queries";
import type { EquipmentRentalLink, PartyType } from "@/lib/types";
import { gridDefaults, FlexGrid } from "@/grids/gridDefaults";
import { HelpHint } from "@/components/HelpHint";
import { useConstructionPack } from "@/lib/useConstructionPack";
import { useToast } from "@/components/Toaster";
import { ProjectIdSelect } from "@/components/ProjectIdSelect";

export const Route = createFileRoute("/_auth/equipment/rental-links")({
  component: RentalLinksPage,
});

function fmtMoney(v: string | number | null | undefined) {
  if (v === null || v === undefined || v === "") return "—";
  const n = typeof v === "string" ? Number(v) : v;
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(n as number);
}

function RentalLinksPage() {
  const { enabled, loading: packLoading } = useConstructionPack();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  const [equipmentId, setEquipmentId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [vendorId, setVendorId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const linksQ = useQuery(
    equipmentRentalLinksQueryOptions({
      equipment_id: equipmentId || undefined,
      project_id: projectId || undefined,
      vendor_id: vendorId || undefined,
      from: from || undefined,
      to: to || undefined,
      limit: 500,
    }),
  );
  const links: EquipmentRentalLink[] = linksQ.data?.items ?? [];

  const equipmentQ = useQuery(equipmentItemsQueryOptions({ active_only: false }));
  const equipment = useMemo(
    () =>
      (equipmentQ.data ?? []).filter(
        (e) => e.ownership_type === "rented" || e.ownership_type === "leased",
      ),
    [equipmentQ.data],
  );
  const vendorsQ = useQuery(
    partiesListQueryOptions({
      party_type: "vendor" as PartyType,
      active_only: true,
    }),
  );
  const vendors = vendorsQ.data ?? [];

  const unlinkMut = useMutation({
    mutationFn: (id: string) =>
      api(`/api/construction/equipment/rental-links/${id}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      toast.success("Rental link removed");
      qc.invalidateQueries({ queryKey: equipmentRentalLinkKeys.all });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const exportCsv = () => {
    const header = [
      "rental_start",
      "rental_end",
      "equipment_code",
      "vendor_name",
      "ap_invoice_no",
      "project_code",
      "cost_code",
      "amount",
      "created_at",
    ];
    const lines = [header.join(",")];
    for (const r of links) {
      const cells = [
        r.rental_start,
        r.rental_end ?? "",
        r.equipment_code ?? "",
        r.vendor_name ?? "",
        r.ap_invoice_no ?? "",
        r.project_code ?? "",
        r.cost_code ?? "",
        r.amount,
        r.created_at,
      ].map((c) => {
        const s = String(c);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      });
      lines.push(cells.join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `equipment-rental-links-${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const cols: GridColDef<EquipmentRentalLink>[] = [
    { field: "rental_start", headerName: "Start", width: 110 },
    {
      field: "rental_end",
      headerName: "End",
      width: 110,
      valueGetter: (_v, r) => r.rental_end ?? "—",
    },
    {
      field: "equipment_code",
      headerName: "Equipment",
      width: 160,
      renderCell: (p: GridRenderCellParams<EquipmentRentalLink>) => (
        <Chip
          size="small"
          variant="outlined"
          label={
            <span>
              <span style={{ fontFamily: "monospace" }}>
                {p.row.equipment_code ?? "?"}
              </span>{" "}
              {p.row.equipment_name ?? ""}
            </span>
          }
        />
      ),
    },
    { field: "vendor_name", headerName: "Vendor", width: 180 },
    {
      field: "ap_invoice_no",
      headerName: "AP invoice",
      width: 140,
      renderCell: (p) =>
        p.row.ap_invoice_id ? (
          <Link
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            to={`/invoices/ap/${p.row.ap_invoice_id}` as any}
            style={{ color: "inherit" }}
          >
            {p.row.ap_invoice_no ?? p.row.ap_invoice_id.slice(0, 8) + "…"}
          </Link>
        ) : (
          "—"
        ),
    },
    { field: "project_code", headerName: "Project", width: 140 },
    { field: "cost_code", headerName: "Cost code", width: 140 },
    {
      field: "amount",
      headerName: "Amount",
      width: 120,
      align: "right",
      headerAlign: "right",
      renderCell: (p) => fmtMoney(p.value as string),
    },
    {
      field: "created_at",
      headerName: "Created",
      width: 170,
      valueGetter: (_v, r) => new Date(r.created_at).toLocaleString(),
    },
    {
      field: "actions",
      headerName: "",
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (p) => (
        <Button
          size="small"
          color="warning"
          disabled={unlinkMut.isPending}
          onClick={() => unlinkMut.mutate(p.row.id)}
        >
          Unlink
        </Button>
      ),
    },
  ];

  if (!packLoading && !enabled) return null;

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Button
            startIcon={<ArrowBackIcon />}
            size="small"
            onClick={() => navigate({ to: "/equipment" })}
          >
            Equipment
          </Button>
          <Typography variant="h5" fontWeight={600}>
            Equipment rentals
          </Typography>
        </Stack>
        <HelpHint
          docPath="equipment-costing/rental-linkage.md"
          label="Rental linkage help"
        />
      </Stack>

      <Stack direction="row" spacing={2} mb={2} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Equipment</InputLabel>
          <Select
            value={equipmentId}
            label="Equipment"
            onChange={(e) => setEquipmentId(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            {equipment.map((e) => (
              <MenuItem key={e.id} value={e.id}>
                {e.code} — {e.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ minWidth: 280 }}>
          <ProjectIdSelect
            value={projectId}
            label="Project"
            onChange={setProjectId}
            statusFilter={["active"]}
            placeholder="All active projects…"
          />
        </Box>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Vendor</InputLabel>
          <Select
            value={vendorId}
            label="Vendor"
            onChange={(e) => setVendorId(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            {vendors.map((v) => (
              <MenuItem key={v.id} value={v.id}>
                {v.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="From"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={from}
          onChange={(e) => setFrom(e.target.value)}
        />
        <TextField
          label="To"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={to}
          onChange={(e) => setTo(e.target.value)}
        />
        <Box sx={{ flex: 1 }} />
        <Button
          startIcon={<DownloadIcon />}
          onClick={exportCsv}
          disabled={links.length === 0}
        >
          Export CSV
        </Button>
      </Stack>

      {linksQ.isLoading && <CircularProgress size={20} />}
      {linksQ.error && <Alert severity="error">Failed to load links.</Alert>}

      <Paper variant="outlined" sx={{ height: "70vh" }}>
        <FlexGrid>
          <DataGridPro<EquipmentRentalLink>
            {...gridDefaults}
            rows={links}
            columns={cols}
            getRowId={(r) => r.id}
            disableRowSelectionOnClick
          />
        </FlexGrid>
      </Paper>
    </Box>
  );
}
